import React, { useState, useEffect } from 'react';
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  Select,
  Option,
  Sheet,
  Input,
  Modal,
  Table,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  Alert,
  IconButton
} from '@mui/joy';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
// Remove direct import of logo.svg since we'll load it dynamically
// import logo from './logo.svg';
import { generateResultPdf, openPdfInNewTab, savePdf, preloadLogoImage, setAppName as setPdfAppName, generateBatchResultPdf, generateVorhaltepauschaleOnlyPdf } from './utils/PdfGenerator';
import Admin from './components/Admin';
import AdminLogin from './components/AdminLogin';
import BsnrVerification from './components/BsnrVerification';
import { checkAuthentication, logout} from './utils/LicenseVerification';
import ManageEvaluations from './pages/ManageEvaluations';

// Import ICD database utilities
import { initializeDatabase, searchIcd, isDatabaseAvailable, loadIcdDatabaseFromServer, getDatabaseStats } from './utils/IcdDatabaseUtils';

// Import the ICD attribution component
import IcdAttribution from './components/IcdAttribution';

// Protected route component to check admin authentication
function ProtectedAdminRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAdminAuth = async () => {
      try {
        const response = await fetch('/api/admin/auth-status');
        const data = await response.json();
        setIsAuthenticated(data.isAuthenticated);
      } catch (error) {
        console.error('Failed to check admin authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminAuth();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Überprüfe Authentifizierung...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page and remember where we were trying to go
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
}

// Create a MainApp component that contains the current App content
function MainApp({ onLogout }) {
  // Get app name from window.appSettings if available, otherwise use default
  const [appName, setAppName] = useState(window.appSettings?.appName || 'QuEry-Tool');
  const [logoUrl, setLogoUrl] = useState('/logo.svg');
  const [evaluation, setEvaluation] = useState('');
  const [evaluations, setEvaluations] = useState([]); // State for evaluations list
  const [files, setFiles] = useState([]); // Changed from single file to array of files
  const [result, setResult] = useState('');
  const [TeaVM, setTeaVM] = useState(null);
  const [isCustomEvaluation, setIsCustomEvaluation] = useState(true); // Set to true for default selection
  
  // Add Demo Mode state - initialized from localStorage if available
  const [demoMode, setDemoMode] = useState(() => {
    const savedMode = localStorage.getItem('queryToolDemoMode');
    return savedMode === 'true';
  });
  
  // Save demo mode preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('queryToolDemoMode', demoMode.toString());
  }, [demoMode]);
  
  const [manualEvaluation, setManualEvaluation] = useState({
    evaluationName: '',
    dateSpanStart: '',
    dateSpanEnd: '',
    ageFrom: 0,
    ageTo: 0,
    ageOperator: 0, // Default: OPERATOR_LESSTHAN
    institutionIds: '',
    institutionIdsOperator: 0, // 0 = UND by default
    baseServices: '',
    baseServicesOperator: 0, // 0 = UND by default
    baseServicesWithinDateSpan: false,
    services: '',
    servicesOperator: 0, // 0 = UND by default
    servicesWithinDateSpan: false,
    notServices: '',
    notServicesOperator: 2, // 2 = Nicht-UND by default
    notServicesWithinDateSpan: false,
    diagnoses: '',
    diagnosesOperator: 0, // 0 = UND by default
    diagnosesWithinDateSpan: false,
    notDiagnoses: '',
    notDiagnosesOperator: 2, // 2 = Nicht-UND by default
    notDiagnosesWithinDateSpan: false,
    excludeIfFoundAcrossQuarters: false,
  });

  const [showAdvancedView, setShowAdvancedView] = useState(false);

  const toggleAdvancedView = () => {
    setShowAdvancedView((prev) => !prev);
  };

  const [services, setServices] = useState([]);
  const [isServicesDialogOpen, setIsServicesDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState('baseServices'); // Which field to update (baseServices, services, notServices)
  const [resultsData, setResultsData] = useState([]); // To store results for PDF generation
  
  const [customEvaluations, setCustomEvaluations] = useState([]); // State for custom evaluations

  // Add state for Vorhaltepauschale
  const [vorhaltepauschaleEnabled, setVorhaltepauschaleEnabled] = useState(false);
  const [vorhaltepauschaleKriterien, setVorhaltepauschaleKriterien] = useState([]);
  const [fileQuarters, setFileQuarters] = useState([]); // Stores quarter for each file
  const [vorhaltepauschaleOnlyMode, setVorhaltepauschaleOnlyMode] = useState(false); // New: standalone Vorhaltepauschale mode

  // State for Beta hint visibility
  const [showBetaHint, setShowBetaHint] = useState(() => {
    const saved = localStorage.getItem('showBetaHint');
    return saved === null ? true : saved === 'true';
  });

  // State for password info dialog
  const [showPasswordInfo, setShowPasswordInfo] = useState(false);

  // Add state for ICD database
  const [icdDatabaseAvailable, setIcdDatabaseAvailable] = useState(false);
  const [icdSearchQuery, setIcdSearchQuery] = useState('');
  const [icdSearchResults, setIcdSearchResults] = useState([]);
  const [showIcdSearchDialog, setShowIcdSearchDialog] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [icdDatabaseLoading, setIcdDatabaseLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [icdLoadingProgress, setIcdLoadingProgress] = useState({ status: 'idle', message: '', progress: 0 });

  // Add state for dynamically loaded logo and app name
  useEffect(() => {
    // Load the WebAssembly runtime and .wasm file
    async function loadWasmRuntime() {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/wasm-gc/backend.wasm-runtime.js'; // Correct path to the public folder
        console.log('Attempting to load runtime from:', script.src); // Debugging log
        script.onload = async () => {
          if (window.TeaVM && window.TeaVM.wasmGC) {
            console.log('TeaVM runtime loaded successfully.');

            try {
              // Load the WebAssembly module with cache busting
              const wasmUrl = `/wasm-gc/backend.wasm?t=${Date.now()}`;
              console.log('Loading WASM from:', wasmUrl);
              const backend = await window.TeaVM.wasmGC.load(wasmUrl);
              console.log('WebAssembly module loaded successfully.');
              setTeaVM({ ...window.TeaVM, backend }); // Store the loaded backend
              resolve();
            } catch (wasmError) {
              console.error('Failed to load the WebAssembly module:', wasmError);
              alert('Failed to load the WebAssembly module. Please check the file path.');
              reject(wasmError);
            }
          } else {
            console.error('TeaVM runtime or wasm property not found after script load.');
            alert('Failed to load the WebAssembly runtime. Please check the runtime file.');
            reject(new Error('TeaVM runtime load failed.'));
          }
        };
        script.onerror = (e) => {
          console.error('Failed to load backend.wasm-runtime.js:', e);
          alert('Failed to load the WebAssembly runtime. Please check the file path and server configuration.');
          reject(e);
        };
        document.body.appendChild(script);
      });
    }

    // Load evaluations into localStorage and state
    async function loadEvaluations() {
      try {
        console.log('Loading evaluations from database');
        
        // Fetch evaluations list first, which gives us all evaluations with their names and content
        let response = await fetch('/api/evaluations');
        
        if (!response.ok) {
          throw new Error('Failed to load evaluations list from database');
        }
        
        const evaluationData = await response.json();
        
        // Extract just the names for the dropdown
        const fileNames = evaluationData.map(item => item.name);
        
        // Set evaluations list in state
        setEvaluations(fileNames);
        
        // Store each evaluation content in localStorage
        evaluationData.forEach(item => {
          if (item.name && item.content) {
            localStorage.setItem(item.name, item.content);
            console.log(`Loaded and stored: ${item.name}`);
          } else {
            console.warn(`Missing name or content for evaluation: ${JSON.stringify(item)}`);
          }
        });
        
        console.log('All evaluations loaded into localStorage from database');
      } catch (error) {
        console.error('Error loading evaluations from database:', error);
        
        // Fall back to loading evaluations.lst file if database approach fails
        try {
          // Try to fetch from React development server first (which will proxy)
          let response = await fetch('/resources/evaluations/evaluations.lst');
          
          // If there's an error, try direct API server as fallback
          if (!response.ok) {
            console.log('Failed to load from proxy, trying API server directly');
            response = await fetch('http://localhost:5001/resources/evaluations/evaluations.lst');
          }
          
          if (!response.ok) {
            throw new Error('Failed to load evaluations list.');
          }
          
          const fileList = await response.text();
          const fileNames = fileList.split('\n').map(name => name.trim()).filter(name => name);
          setEvaluations(fileNames); // Set evaluations list in state

          for (const fileName of fileNames) {
            // Try to fetch from React development server first (which will proxy)
            let fileResponse = await fetch(`/resources/evaluations/${fileName}`);
            
            // If there's an error, try direct API server as fallback
            if (!fileResponse.ok) {
              fileResponse = await fetch(`http://localhost:5001/resources/evaluations/${fileName}`);
            }
            
            if (!fileResponse.ok) {
              console.warn(`Failed to load file: ${fileName}`);
              continue;
            }
            
            const fileContent = await fileResponse.text();
            localStorage.setItem(fileName, fileContent);
            console.log(`Loaded and stored: ${fileName}`);
          }

          console.log('All evaluations loaded into localStorage from files (fallback)');
        } catch (fallbackError) {
          console.error('Error with fallback loading method:', fallbackError);
        }
      }
    }

    // Load services from services.lst
    async function loadServices() {
      try {
        // Try to fetch from React development server first (which will proxy)
        let response = await fetch('/resources/services.lst');
        
        // If there's an error, try direct API server as fallback
        if (!response.ok) {
          response = await fetch('http://localhost:5001/resources/services.lst');
        }
        
        if (!response.ok) {
          throw new Error('Failed to load services list.');
        }
        
        const servicesText = await response.text();
        
        // Parse the services format: code$description|code$description
        const servicesList = servicesText.split('|').map(item => {
          const [code, description] = item.split('$');
          return { code, description };
        });
        
        setServices(servicesList);
        console.log('Services loaded:', servicesList);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    }
    
    // Load appearance settings
    async function loadAppearanceSettings() {
      try {
        const response = await fetch('/api/appearance/settings');
        if (response.ok) {
          const settings = await response.json();
          if (settings.appName) {
            setAppName(settings.appName);
            // Set the app name in the PDF generator
            setPdfAppName(settings.appName);
          }
        }
      } catch (error) {
        console.error('Error loading appearance settings:', error);
      }
    }

    // Load Vorhaltepauschale criteria
    async function loadVorhaltepauschaleKriterien() {
      try {
        const response = await fetch('/api/vorhaltepauschale-kriterien/public');
        if (response.ok) {
          const kriterien = await response.json();
          setVorhaltepauschaleKriterien(kriterien);
        }
      } catch (error) {
        console.error('Error loading Vorhaltepauschale Kriterien:', error);
      }
    }

    // Set logo URL with cache busting parameter
    const newLogoUrl = `/logo.svg?t=${Date.now()}`;
    setLogoUrl(newLogoUrl);

    // Sequentially load WebAssembly runtime, evaluations, and preload the logo image
    async function initializeApp() {
      try {
        await loadWasmRuntime();
        await loadEvaluations();
        await loadServices();
        await loadAppearanceSettings();
        await loadVorhaltepauschaleKriterien();
        
        // Use a direct reference to the logo with absolute URL for PDF
        const fullLogoUrl = window.location.origin + newLogoUrl;
        console.log('Attempting to preload logo for PDF with full URL:', fullLogoUrl);
        await preloadLogoImage(fullLogoUrl);
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    }

    initializeApp();

    return () => {
      const script = document.querySelector('script[src="/wasm-gc/backend.wasm-runtime.js"]');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []); // Empty dependency array ensures this runs only once when the component mounts

  // Fetch custom evaluations for the authenticated BSNR
  useEffect(() => {
    async function loadCustomEvaluations() {
      try {
        // Check if authenticated first
        const isAuthenticated = await checkAuthentication();
        if (!isAuthenticated) {
          console.log("Not authenticated, skipping custom evaluations load");
          return;
        }
        
        const response = await fetch('/api/custom-evaluations', { 
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log("Loaded custom evaluations:", data);
          setCustomEvaluations(data);
        } else if (response.status === 401) {
          console.log("Unauthorized access to custom evaluations");
          // User not authenticated, could redirect to verification
        } else {
          console.error("Error loading custom evaluations:", response.statusText);
        }
      } catch (error) {
        console.error('Error loading custom evaluations:', error);
      }
    }

    loadCustomEvaluations();
  }, []);

  // Initialize and load ICD database
  useEffect(() => {
    async function initIcdDatabase() {
      try {
        setIcdDatabaseLoading(true);
        setIcdLoadingProgress({ status: 'starting', message: 'Initialisiere ICD Datenbank...', progress: 0 });
        
        // Initialize the database
        await initializeDatabase();
        
        // Check if the database already has data locally
        const stats = await getDatabaseStats();
        if (stats.localCount > 0) {
          console.log(`ICD database already loaded with ${stats.localCount} entries`);
          setIcdDatabaseAvailable(true);
          setIcdLoadingProgress({ 
            status: 'complete', 
            message: `ICD Datenbank bereit mit ${stats.localCount.toLocaleString()} Einträgen`, 
            progress: 100 
          });
          setIcdDatabaseLoading(false);
          return;
        }
        
        // If no local data, check if server has data and load it
        const available = await isDatabaseAvailable();
        
        if (available) {
          console.log('ICD database available on server, loading into browser...');
          setIcdLoadingProgress({ 
            status: 'loading', 
            message: 'Lade ICD Datenbank vom Server...', 
            progress: 10 
          });
          
          // Load the database from server to IndexedDB
          const loaded = await loadIcdDatabaseFromServer(false, (progress) => {
            setIcdLoadingProgress(progress);
          });
          
          setIcdDatabaseAvailable(loaded);
          console.log('ICD database loading completed, available =', loaded);
        } else {
          console.log('ICD database is not available on server');
          setIcdDatabaseAvailable(false);
          setIcdLoadingProgress({ 
            status: 'error', 
            message: 'ICD Datenbank ist nicht verfügbar', 
            progress: 100 
          });
        }
      } catch (error) {
        console.error('Error initializing ICD database:', error);
        setIcdDatabaseAvailable(false);
        setIcdLoadingProgress({ 
          status: 'error', 
          message: `Fehler beim Laden der ICD Datenbank: ${error.message}`, 
          progress: 100 
        });
      } finally {
        setIcdDatabaseLoading(false);
      }
    }
    
    initIcdDatabase();
  }, []);

  // Save a custom evaluation
  const saveCustomEvaluation = async (name, content) => {
    try {
      const response = await fetch('/api/custom-evaluations', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include', // Ensure cookies are sent with the request
        body: JSON.stringify({ name, content }),
      });

      if (response.ok) {
        alert(`Auswertung "${name}" wurde gespeichert.`);
        
        // Refresh the custom evaluations list
        const updatedEvaluationsResponse = await fetch('/api/custom-evaluations', { 
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (updatedEvaluationsResponse.ok) {
          const updatedEvaluations = await updatedEvaluationsResponse.json();
          setCustomEvaluations(updatedEvaluations);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        alert(`Fehler beim Speichern: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving custom evaluation:', error);
      alert('Fehler beim Speichern der Auswertung');
    }
  };

  // Delete a custom evaluation
  const deleteCustomEvaluation = async (id) => {
    try {
      const response = await fetch(`/api/custom-evaluations/${id}`, { 
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Auswertung wurde gelöscht.');
        setCustomEvaluations((prev) => prev.filter((evaluation) => evaluation.id !== id));
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        alert(`Fehler beim Löschen: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting custom evaluation:', error);
      alert('Fehler beim Löschen der Auswertung');
    }
  };

  // Add a handler for deleting custom evaluations
  const handleDeleteCustomEvaluation = (id) => {
    if (window.confirm('Möchten Sie diese benutzerdefinierte Auswertung wirklich löschen?')) {
      deleteCustomEvaluation(id);
    }
  };

  // Merge standard and custom evaluations for the dropdown
  const mergedEvaluations = [
    ...customEvaluations.map((evaluation) => ({ name: evaluation.name, isCustom: true })),
    ...evaluations.map((name) => ({ name, isCustom: false })),
  ];

  // Extract quarter from filename (e.g., "file_Q2_2024.con" -> 2)
  const extractQuarterFromFilename = (filename) => {
    // Try pattern: _Q[1-4]_ or -Q[1-4]- or Q[1-4].
    const match = filename.match(/[_\-\s]Q([1-4])[_\-\s\.]/i);
    if (match) {
      return parseInt(match[1]);
    }
    // Try simple pattern: Q[1-4] anywhere in filename
    const simpleMatch = filename.match(/Q([1-4])/i);
    if (simpleMatch) {
      return parseInt(simpleMatch[1]);
    }
    return 1; // Default to Q1
  };

  // Handler for traditional file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    // Limit to max 4 files total
    if (files.length + selectedFiles.length > 4) {
      alert('Maximal 4 Dateien können ausgewählt werden.');
      return;
    }

    setFiles(prev => [...prev, ...selectedFiles]);
    // Extract quarters from filenames
    const extractedQuarters = selectedFiles.map(file => extractQuarterFromFilename(file.name));
    setFileQuarters(prev => [...prev, ...extractedQuarters]);
  };

  // Remove a file from selection
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileQuarters(prev => prev.filter((_, i) => i !== index));
  };

  // Update quarter for a specific file
  const updateFileQuarter = (index, quarter) => {
    setFileQuarters(prev => {
      const updated = [...prev];
      updated[index] = quarter;
      return updated;
    });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files || []);

    // Limit to max 4 files total
    if (files.length + droppedFiles.length > 4) {
      alert('Maximal 4 Dateien können ausgewählt werden.');
      return;
    }

    setFiles(prev => [...prev, ...droppedFiles]);
    // Extract quarters from filenames
    const extractedQuarters = droppedFiles.map(file => extractQuarterFromFilename(file.name));
    setFileQuarters(prev => [...prev, ...extractedQuarters]);
  };

  const handleManualInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setManualEvaluation((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const serializeManualEvaluation = (manualEval) => {
    return [
      manualEval.evaluationName.trim(),
      manualEval.dateSpanStart,
      manualEval.dateSpanEnd,
      manualEval.ageFrom,
      manualEval.ageTo,
      manualEval.ageOperator,
      manualEval.institutionIds.trim(),
      manualEval.institutionIdsOperator,
      manualEval.baseServices.trim(),
      manualEval.baseServicesOperator,
      manualEval.baseServicesWithinDateSpan ? "1" : "0",
      manualEval.services.trim(),
      manualEval.servicesOperator,
      manualEval.servicesWithinDateSpan ? "1" : "0",
      manualEval.notServices.trim(),
      manualEval.notServicesOperator,
      manualEval.notServicesWithinDateSpan ? "1" : "0",
      manualEval.diagnoses.trim(),
      manualEval.diagnosesOperator,
      manualEval.diagnosesWithinDateSpan ? "1" : "0",
      manualEval.notDiagnoses.trim(),
      manualEval.notDiagnosesOperator,
      manualEval.notDiagnosesWithinDateSpan ? "1" : "0",
      manualEval.excludeIfFoundAcrossQuarters ? "1" : "0",
    ].join('|');
  };

  const deserializeEvaluation = (serializedEvaluation) => {
    const objectMember = serializedEvaluation.split('|');

    if (objectMember.length < 23) {
      alert('Die Auswertungsdatei enthält nicht alle erforderlichen Felder.');
      return null;
    }

    try {
      return {
        evaluationName: objectMember[0],
        dateSpanStart: objectMember[1],
        dateSpanEnd: objectMember[2],
        ageFrom: parseInt(objectMember[3]),
        ageTo: parseInt(objectMember[4]),
        ageOperator: parseInt(objectMember[5]),
        institutionIds: objectMember[6],
        institutionIdsOperator: parseInt(objectMember[7]),
        baseServices: objectMember[8],
        baseServicesOperator: parseInt(objectMember[9]),
        baseServicesWithinDateSpan: objectMember[10] === '1',
        services: objectMember[11],
        servicesOperator: parseInt(objectMember[12]),
        servicesWithinDateSpan: objectMember[13] === '1',
        notServices: objectMember[14],
        notServicesOperator: parseInt(objectMember[15]),
        notServicesWithinDateSpan: objectMember[16] === '1',
        diagnoses: objectMember[17],
        diagnosesOperator: parseInt(objectMember[18]),
        diagnosesWithinDateSpan: objectMember[19] === '1',
        notDiagnoses: objectMember[20],
        notDiagnosesOperator: parseInt(objectMember[21]),
        notDiagnosesWithinDateSpan: objectMember[22] === '1',
        excludeIfFoundAcrossQuarters: objectMember.length > 23 && objectMember[23] === '1',
      };
    } catch (error) {
      alert('Einzelne Parameter der Auswertungsdatei können nicht verarbeitet werden.');
      return null;
    }
  };

  // Normalize older evaluation strings (missing the new cross-quarter flag) to ensure compatibility in batch mode
  const normalizeEvaluationContent = (content) => {
    if (!content || typeof content !== 'string') return content;
    const parts = content.split('|');
    if (parts.length < 23) {
      // pad missing fields up to the legacy 23, then append default for the new flag
      while (parts.length < 23) parts.push('');
      parts.push('0');
      return parts.join('|');
    }
    if (parts.length === 23) {
      // append default "0" for excludeIfFoundAcrossQuarters
      parts.push('0');
      return parts.join('|');
    }
    // already includes the new flag or more; return as-is
    return content;
  };

  // Deserialize evaluation content and prepare for processing
  const prepareEvaluationContent = (evaluationName) => {
    // Check if this is a custom evaluation from our database
    const customEval = customEvaluations.find(e => e.name === evaluationName);

    if (customEval) {
      return normalizeEvaluationContent(customEval.content);
    } else {
      // Otherwise look in localStorage for standard evaluations
      const serializedEvaluation = localStorage.getItem(evaluationName);
      if (serializedEvaluation) {
        return normalizeEvaluationContent(serializedEvaluation);
      }
    }

    console.error(`Evaluation content not found for: ${evaluationName}`);
    return null;
  };

  // Handle evaluation selection change
  const handleEvaluationSelect = (newValue) => {
    if (newValue === "custom") {
      // Handle custom evaluation selection
      setIsCustomEvaluation(true);
      setEvaluation("");
      // Reset manual evaluation to defaults with proper operator values
      setManualEvaluation({
        evaluationName: '',
        dateSpanStart: '',
        dateSpanEnd: '',
        ageFrom: 0,
        ageTo: 0,
        ageOperator: 0, // OPERATOR_LESSTHAN
        institutionIds: '',
        institutionIdsOperator: 0, // UND
        baseServices: '',
        baseServicesOperator: 0, // UND
        baseServicesWithinDateSpan: false,
        services: '',
        servicesOperator: 0, // UND
        servicesWithinDateSpan: false,
        notServices: '',
        notServicesOperator: 2, // Nicht-UND
        notServicesWithinDateSpan: false,
        diagnoses: '',
        diagnosesOperator: 0, // UND
        diagnosesWithinDateSpan: false,
        notDiagnoses: '',
        notDiagnosesOperator: 2, // Nicht-UND
        notDiagnosesWithinDateSpan: false,
        excludeIfFoundAcrossQuarters: false,
      });
    } else {
      // Handle regular or custom evaluation selection
      setIsCustomEvaluation(false);
      setEvaluation(newValue);

      if (newValue) {
        // First check if this is a custom evaluation from our database
        const customEval = customEvaluations.find(e => e.name === newValue);
                
        if (customEval) {
          // Use the content from the custom evaluation
          const deserializedEvaluation = deserializeEvaluation(customEval.content);
          if (deserializedEvaluation) {
            setManualEvaluation({
              ...deserializedEvaluation,
              id: customEval.id // Keep the ID for potential updates/deletes
            });
            
            // Check if the evaluation has any advanced parameters filled
            // If yes, automatically show the advanced view
            const hasAdvancedParameters = 
              deserializedEvaluation.dateSpanStart || 
              deserializedEvaluation.dateSpanEnd || 
              deserializedEvaluation.baseServicesWithinDateSpan || 
              deserializedEvaluation.servicesWithinDateSpan || 
              deserializedEvaluation.notServicesWithinDateSpan ||
              deserializedEvaluation.institutionIds ||
              deserializedEvaluation.ageFrom > 0 ||
              deserializedEvaluation.ageTo > 0;
              
            if (hasAdvancedParameters) {
              setShowAdvancedView(true);
            }
          }
        } else {
          // Otherwise look in localStorage for standard evaluations
          const serializedEvaluation = localStorage.getItem(newValue);
          if (serializedEvaluation) {
            const deserializedEvaluation = deserializeEvaluation(serializedEvaluation);
            if (deserializedEvaluation) {
              setManualEvaluation(deserializedEvaluation);
              
              // Check if the evaluation has any advanced parameters filled
              // If yes, automatically show the advanced view
              const hasAdvancedParameters = 
                deserializedEvaluation.dateSpanStart || 
                deserializedEvaluation.dateSpanEnd || 
                deserializedEvaluation.baseServicesWithinDateSpan || 
                deserializedEvaluation.servicesWithinDateSpan || 
                deserializedEvaluation.notServicesWithinDateSpan ||
                deserializedEvaluation.institutionIds ||
                deserializedEvaluation.ageFrom > 0 ||
                deserializedEvaluation.ageTo > 0;
                
              if (hasAdvancedParameters) {
                setShowAdvancedView(true);
              }
            }
          } else {
            alert('Die ausgewählte Auswertung konnte nicht geladen werden.');
          }
        }
      }
    }
  };

  // Update handleSaveCustomEvaluation to use saveCustomEvaluation
  const handleSaveCustomEvaluation = () => {
    if (!manualEvaluation.evaluationName || manualEvaluation.evaluationName.trim() === '') {
      alert('Bitte geben Sie einen Namen für die benutzerdefinierte Auswertung ein.');
      return;
    }

    const evaluationContent = serializeManualEvaluation(manualEvaluation);
    saveCustomEvaluation(manualEvaluation.evaluationName.trim(), evaluationContent);
  };

  // Helper function to parse the result string from the backend
  const parseResultString = (resultString) => {
    if (!resultString || typeof resultString !== 'string') {
      return { error: 'Ungültiges Ergebnis', totalPatients: 0, matchingPatients: 0, patients: [] };
    }
    
    if (resultString.startsWith('ERROR:')) {
      return { error: resultString.substring(6).trim(), totalPatients: 0, matchingPatients: 0, patients: [] };
    }
    
    // Parse the TOTAL and MATCHING counts from the result string - they should always be present
    let totalPatients = 0;
    let matchingPatients = 0;
    let infoMessage = null;
    
    // Extract total patients count using regex - should always be in the format TOTAL:X
    const totalMatch = resultString.match(/TOTAL:(\d+)/);
    if (totalMatch && totalMatch.length > 1) {
      totalPatients = parseInt(totalMatch[1], 10);
      console.log(`Parsed total patients: ${totalPatients} from match: ${totalMatch[0]}`);
    } else {
      console.warn('Failed to parse total patients count from:', resultString);
    }
    
    // Extract matching patients count using regex - should always in the format MATCHING:X
    const matchingMatch = resultString.match(/MATCHING:(\d+)/);
    if (matchingMatch && matchingMatch.length > 1) {
      matchingPatients = parseInt(matchingMatch[1], 10);
      console.log(`Parsed matching patients: ${matchingPatients} from match: ${matchingMatch[0]}`);
    } else {
      console.warn('Failed to parse matching patients count from:', resultString);
    }
    
    // Check for INFO message
    const infoMatch = resultString.match(/INFO:([^|]+)/);
    if (infoMatch && infoMatch.length > 1) {
      infoMessage = infoMatch[1].trim();
      console.log(`Found INFO message: ${infoMessage}`);
    }
    
    // The INFO prefix is now secondary - we've already extracted TOTAL and MATCHING
    // This handles the case where INFO comes first in the string
    if (resultString.startsWith('INFO:')) {
      const startInfo = resultString.substring(5).trim();
      let infoText = startInfo;
      
      // If there's a pipe, extract just the info text portion
      if (startInfo.includes('|')) {
        infoText = startInfo.split('|')[0].trim();
      }
      
      return { 
        info: infoText, 
        totalPatients, // Use what we extracted above
        matchingPatients, // Use what we extracted above
        patients: [] 
      };
    }
    
    // Parse patient data if available
    let patients = [];
    const patientsMatch = resultString.match(/PATIENTS:\[(.*)\]/s);
    if (patientsMatch && patientsMatch.length > 1) {
      try {
        // Parse the JSON array
        const patientsJson = '[' + patientsMatch[1] + ']';
        patients = JSON.parse(patientsJson);
        console.log(`Successfully parsed ${patients.length} patients`);
      } catch (e) {
        console.error('Error parsing patient data:', e);
      }
    }
    
    // If we have an INFO message but no error, include it in the result
    if (infoMessage) {
      return { 
        info: infoMessage,
        totalPatients, 
        matchingPatients, 
        patients 
      };
    }
    
    return { totalPatients, matchingPatients, patients };
  };

  // Helper function to anonymize patient data
  const anonymizePatientData = (patients) => {
    if (!patients || !demoMode) return patients;
    
    return patients.map((patient, index) => ({
      ...patient,
      firstname: `Vorname${index + 1}`,
      lastname: `Nachname${index + 1}`
    }));
  };

  // Handler for Vorhaltepauschale-only mode
  const handleVorhaltepauschaleOnlySubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      alert('Bitte wählen Sie mindestens eine Datei aus.');
      return;
    }

    if (!TeaVM || !TeaVM.backend) {
      alert('WebAssembly runtime or module is not loaded yet.');
      return;
    }

    try {
      const results = [];
      const bsnrToUse = localStorage.getItem('authenticatedBsnr') || '';

      // Load base GOPs configuration
      let baseGopCodes = ["03001", "03002", "03003", "03004", "03005"]; // Default
      try {
        const configResponse = await fetch('/api/vorhaltepauschale-config/public');
        if (configResponse.ok) {
          const config = await configResponse.json();
          if (config.base_gop_codes) {
            baseGopCodes = JSON.parse(config.base_gop_codes);
          }
        }
      } catch (err) {
        console.warn('Could not load base GOP config, using defaults');
      }

      // Prepare criteria JSON with base GOPs
      const kriterienWithConfig = {
        kriterien: vorhaltepauschaleKriterien,
        baseGopCodes: baseGopCodes
      };
      const kriterienJson = JSON.stringify(kriterienWithConfig);

      // Process each file with its corresponding quarter
      for (let i = 0; i < files.length; i++) {
        try {
          const file = files[i];
          const quarter = fileQuarters[i] || 1;

          // Read file content
          const fileContent = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const decoder = new TextDecoder('ISO-8859-15');
              resolve(decoder.decode(reader.result));
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
          });

          // Call WebAssembly backend to calculate Vorhaltepauschale only
          const vorhaltepauschaleResult = TeaVM.backend.instance.exports.calculateVorhaltepauschale.value(
            fileContent,
            kriterienJson,
            quarter,
            bsnrToUse
          );

          const vorhaltepauschaleData = JSON.parse(vorhaltepauschaleResult);

          // Check for errors
          if (vorhaltepauschaleData.error) {
            if (vorhaltepauschaleData.error.includes('BSNR')) {
              alert(`Fehler bei der Datei "${file.name}": ${vorhaltepauschaleData.error}`);
              continue;
            }
          }

          // Store result with filename
          results.push({
            name: file.name,
            vorhaltepauschale: vorhaltepauschaleData
          });
        } catch (error) {
          console.error(`Error calculating Vorhaltepauschale for file ${i}:`, error);
          alert(`Fehler bei der Verarbeitung von "${files[i].name}": ${error.message}`);
        }
      }

      // Store results for potential later use
      setResultsData(results);

      // Generate and display PDF with Vorhaltepauschale-only data
      const pdfDoc = generateVorhaltepauschaleOnlyPdf(results, demoMode);

      // Generate filename based on whether single or multiple files
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = results.length === 1
        ? `Vorhaltepauschale_${results[0].name.replace(/\.[^/.]+$/, '')}_${timestamp}.pdf`
        : `Vorhaltepauschale_Mehrfachauswertung_${timestamp}.pdf`;

      // Open PDF in new tab
      openPdfInNewTab(pdfDoc, filename);

      // Show success message
      setResult(`Vorhaltepauschale-Auswertung erfolgreich abgeschlossen für ${results.length} Datei${results.length !== 1 ? 'en' : ''}.`);

    } catch (error) {
      console.error('Error in Vorhaltepauschale-only mode:', error);
      setResult(`Fehler: ${error.message}`);
      alert(`Ein Fehler ist aufgetreten: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      alert('Bitte wählen Sie mindestens eine Datei aus.');
      return;
    }

    if (!TeaVM || !TeaVM.backend) {
      alert('WebAssembly runtime or module is not loaded yet.');
      return;
    }

    try {
      const results = [];
      
      // Get the authenticated BSNR if available, but don't require it
      // Just use what's in localStorage or an empty string if not available
      const bsnrToUse = localStorage.getItem('authenticatedBsnr') || '';
      
      // Process each file
      for (const file of files) {
        // Read the file as a string with ISO-8859-15 encoding
        const fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const decoder = new TextDecoder('ISO-8859-15');
            resolve(decoder.decode(reader.result));
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsArrayBuffer(file);
        });

        let evaluationContent;
        if (isCustomEvaluation || manualEvaluation.evaluationName) {
          // Serialize manual evaluation into the required format
          evaluationContent = serializeManualEvaluation(manualEvaluation);
        } else {
          // Otherwise, use the selected evaluation
          const normalized = prepareEvaluationContent(evaluation);
          evaluationContent = normalized;
          if (!evaluationContent) {
            alert('Selected evaluation not found in localStorage.');
            return;
          }
        }

        // Call the WebAssembly backend with the file content, evaluation content, and BSNR
        console.log(`Processing file: ${file.name}`);
        const result = TeaVM.backend.instance.exports.process.value(fileContent, evaluationContent, bsnrToUse);
        console.log(`Raw result from backend for ${file.name}:`, result);
        
        // Parse the result string which now contains detailed patient information
        const parsedResult = parseResultString(result);
        console.log(`Parsed result for ${file.name}:`, parsedResult);
        
        // Check if there's a license error
        if (parsedResult.error && parsedResult.error.includes('BSNR')) {
          alert(`Fehler bei der Datei "${file.name}": ${parsedResult.error}`);
          continue; // Skip this file and continue with next
        }
        
        // Ensure totalPatients is never undefined and has a sensible value
        if (parsedResult.totalPatients === undefined) {
          // Fix the syntax error - remove the incorrect comma in template literal
          console.warn(`Missing total patient count for ${file.name} - checking result string directly`);
          
          // Try to extract totalPatients directly from the raw result if needed
          const directMatch = result.match(/TOTAL:(\d+)/);
          if (directMatch && directMatch.length > 1) {
            parsedResult.totalPatients = parseInt(directMatch[1], 10);
            console.log(`Directly extracted total patients: ${parsedResult.totalPatients}`);
          } else if (parsedResult.matchingPatients > 0) {
            // If we have matching patients but no total, use matching as fallback
            parsedResult.totalPatients = parsedResult.matchingPatients;
          }
        }
        
        // Special case for debugging the file with zero counts
        if (parsedResult.totalPatients === 0) {
          console.warn(`File with zero patients: ${file.name}. Raw result: ${result}`);
        }
        
        // Make sure totalPatients is never less than matchingPatients
        if (parsedResult.totalPatients < parsedResult.matchingPatients) {
          // Fix the second syntax error - remove the incorrect comma in template literal
          console.warn(`Invalid total patient count for ${file.name} - fixing...`);
          parsedResult.totalPatients = parsedResult.matchingPatients;
        }
        
        // Apply anonymization if demo mode is active
        if (demoMode && parsedResult.patients) {
          parsedResult.patients = anonymizePatientData(parsedResult.patients);
        }
        
        // Store parsed result with filename
        results.push({ 
          name: file.name, 
          ...parsedResult
        });
      }
      
      // Wenn alle Dateien verarbeitet sind: Schnittmenge bilden (falls Flag aktiv)
      if (manualEvaluation.excludeIfFoundAcrossQuarters) {
        console.log('[CrossQuarter] Starte Schnittmengen-Berechnung über alle Dateien');
        const patientKey = (p) => `${p.firstname}|${p.lastname}|${p.birthdate}`;
        
        // Baue für jede Datei ein Set der Patientenschlüssel
        const patientKeySets = results.map(r => new Set(
          (r.patients || []).map(p => patientKey(p))
        ));
        
        if (patientKeySets.length > 0) {
          // Schnittmenge initial mit erster Datei
            let intersection = new Set(patientKeySets[0]);
            for (let i = 1; i < patientKeySets.length; i++) {
              intersection = new Set([...intersection].filter(k => patientKeySets[i].has(k)));
            }
            console.log('[CrossQuarter] Schnittmenge Schlüssel:', [...intersection]);
            
            // Jede Ergebnisliste auf die Schnittmenge kürzen
            results.forEach(r => {
              if (r.patients) {
                r.patients = r.patients.filter(p => intersection.has(patientKey(p)));
                r.matchingPatients = r.patients.length;
              } else {
                r.matchingPatients = 0;
              }
            });
        } else {
          console.log('[CrossQuarter] Keine Patientendaten vorhanden – Schnittmenge leer');
          results.forEach(r => {
            r.patients = [];
            r.matchingPatients = 0;
          });
        }
      }

      // Store the results for PDF generation
      setResultsData(results);

      // Format results from all files with patient details
      const formattedResults = results.map(r => {
        if (r.error) {
          return `Datei: ${r.name}\nFehler: ${r.error}\n`;
        }
        
        let output = `Datei: ${r.name}\n`;
        output += `Gesamtpatienten: ${r.totalPatients}\n`;
        output += `Passende Patienten: ${r.matchingPatients}\n`;
        
        if (r.patients && r.patients.length > 0) {
          output += '\nPatientenliste:\n';
          for (let idx = 0; idx < r.patients.length; idx++) {
            const patient = r.patients[idx];
            // Format birthdate if it's in YYYYMMDD format
            let birthdate = patient.birthdate || "";
            if (birthdate && /^\d{8}$/.test(birthdate)) {
              const year = birthdate.substring(0, 4);
              const month = birthdate.substring(4, 6);
              const day = birthdate.substring(6, 8);
              birthdate = `${day}.${month}.${year}`;
            }
            
            output += `${idx + 1}. ${patient.firstname} ${patient.lastname}, `;
            output += `Alter: ${patient.age} (geb. ${birthdate})\n`;
          }
        }
         
        return output;
      }).join('\n\n');
      
      setResult(formattedResults);
      
      // Automatically generate PDF after results are processed with a small delay
      // to ensure state updates have completed
      setTimeout(() => {
        console.log('Auto-generating PDF after evaluation');
        // First update the app name in the PDF generator
        setPdfAppName(appName);
        
        // Get the authenticated BSNR if available
        const bsnr = localStorage.getItem('authenticatedBsnr') || '';
        
        // Create timestamp in format DD-MM-YYYY_HH-MM
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const timestamp = `${day}-${month}-${year}_${hours}-${minutes}`;
        
        // Create a filename with timestamp, BSNR, and evaluation name
        // Format: QuEry-Tool_Auswertung_[EvaluationName]_[BSNR]_[TIMESTAMP].pdf
        let evaluationName = manualEvaluation.evaluationName || 'Auswertung';
        // Replace illegal characters from the evaluation name for a safe filename
        evaluationName = evaluationName.replace(/[\\/:*?"<>|]/g, '_');
        
        const filename = `${appName}_Auswertung_${evaluationName}${bsnr ? '_BSNR-' + bsnr : ''}_${timestamp}.pdf`;
        
        // Ensure logo is preloaded with absolute URL just before generating PDF
        const fullLogoUrl = window.location.origin + logoUrl;
        console.log('Preloading logo for automatic PDF generation:', fullLogoUrl);
        
        preloadLogoImage(fullLogoUrl)
          .then(() => {
            console.log('Logo successfully preloaded for automatic PDF generation');
            const pdf = generateResultPdf(results, manualEvaluation, demoMode);
            // Set PDF document properties
            pdf.setProperties({
              title: `${appName} - ${evaluationName} - ${timestamp}`,
              creator: appName,
              author: appName
            });
            // Open in new tab with the formatted filename
            openPdfInNewTab(pdf, filename);
          })
          .catch(error => {
            console.error('Error preloading logo for automatic PDF:', error);
            // Generate PDF anyway, it will use fallback for logo
            const pdf = generateResultPdf(results, manualEvaluation, demoMode);
            // Set PDF document properties
            pdf.setProperties({
              title: `${appName} - ${evaluationName} - ${timestamp}`,
              creator: appName,
              author: appName
            });
            // Open in new tab with the formatted filename
            openPdfInNewTab(pdf, filename);
          });
      }, 500); // Small delay to ensure state is updated

    } catch (error) {
      console.error('Error handling WebAssembly module:', error);
      alert('Fehler bei der Verarbeitung der Dateien: ' + error.message);
    }
  };

  // Update just the PDF generation functions

  const handleGeneratePdf = () => {
    if (resultsData.length === 0) {
      alert('Bitte führen Sie zuerst eine Auswertung durch.');
      return;
    }
    
    // First update the app name in the PDF generator
    setPdfAppName(appName);
    console.log('PDF generation: Updated app name to', appName);
    
    // Ensure logo is preloaded with absolute URL just before generating PDF
    const fullLogoUrl = window.location.origin + logoUrl;
    console.log('Reloading logo before PDF generation:', fullLogoUrl);
    
    preloadLogoImage(fullLogoUrl)
      .then(() => {
        console.log('Logo successfully preloaded for PDF generation');
        // Pass demo mode flag to the PDF generation function
        const pdf = generateResultPdf(resultsData, manualEvaluation, demoMode);
        openPdfInNewTab(pdf);
      })
      .catch(error => {
        console.error('Error preloading logo for PDF:', error);
        // Generate PDF anyway, it will use fallback for logo
        const pdf = generateResultPdf(resultsData, manualEvaluation, demoMode);
        openPdfInNewTab(pdf);
      });
  };

  const handleDownloadPdf = () => {
    if (resultsData.length === 0) {
      alert('Bitte führen Sie zuerst eine Auswertung durch.');
      return;
    }
    
    // First update the app name in the PDF generator
    setPdfAppName(appName);
    console.log('PDF download: Updated app name to', appName);
        
    // Get the authenticated BSNR if available
    const bsnr = localStorage.getItem('authenticatedBSNR') || '';
    
    // Create timestamp in format DD-MM-YYYY_HH-MM
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const timestamp = `${day}-${month}-${year}_${hours}-${minutes}`;
    
    // Create a filename with timestamp, BSNR, and evaluation name
    // Format: QuEry-Tool_Auswertung_[EvaluationName]_[BSNR]_[TIMESTAMP].pdf
    let evaluationName = manualEvaluation.evaluationName || 'Auswertung';
    // Replace illegal characters from the evaluation name for a safe filename
    evaluationName = evaluationName.replace(/[\\/:*?"<>|]/g, '_');
    
    const filename = `${appName}_Auswertung_${evaluationName}${bsnr ? '_BSNR-' + bsnr : ''}_${timestamp}.pdf`;
    
    // Ensure logo is preloaded with absolute URL just before generating PDF
    const fullLogoUrl = window.location.origin + logoUrl;
    console.log('Reloading logo before PDF download:', fullLogoUrl);
    
    preloadLogoImage(fullLogoUrl)
      .then(() => {
        console.log('Logo successfully preloaded for PDF download');
        // Pass demo mode flag to the PDF generation function
        const pdf = generateResultPdf(resultsData, manualEvaluation, demoMode);
        savePdf(pdf, filename);
      })
      .catch(error => {
        console.error('Error preloading logo for PDF:', error);
        // Generate PDF anyway, it will use fallback for logo
        const pdf = generateResultPdf(resultsData, manualEvaluation, demoMode);
        savePdf(pdf, filename);
      });
  };

  // Helper function to ensure radio buttons always have a value
  const handleOperatorChange = (e, newValue) => {
    const name = e.target.name;
    setManualEvaluation(prev => ({
      ...prev,
      [name]: parseInt(newValue)
    }));
  };

  // Handle age operator changes coming from the dropdown
  const handleAgeOperatorChange = (e, newValue) => {
    setManualEvaluation(prev => ({
      ...prev, // Keep all existing properties
      ageOperator: parseInt(newValue)
    }));
  };

  // Function to handle service selection
  const handleServiceSelect = (service) => {
    setManualEvaluation(prev => {
      // Check if service code is already in the field
      const currentCodes = prev[selectedField].split(',').map(code => code.trim()).filter(Boolean);
      
      if (!currentCodes.includes(service.code)) {
        // Add the service code if it's not already there
        const newValue = currentCodes.length > 0 
          ? `${prev[selectedField]}, ${service.code}`
          : service.code;
        
        return {
          ...prev,
          [selectedField]: newValue
        };
      }
      
      // Return unchanged if already included
      return prev;
    });
    
    // Close the dialog after selection
    setIsServicesDialogOpen(false);
  };

  // Function to open service selection dialog for a specific field
  const openServicesDialog = (fieldName) => {
    setSelectedField(fieldName);
    setIsServicesDialogOpen(true);
  };

  // Function to handle ICD search
  const handleIcdSearch = async (query) => {
    if (!query) {
      setIcdSearchResults([]);
      return;
    }
    
    try {
      const results = await searchIcd(query, { limit: 30 });
      setIcdSearchResults(results);
    } catch (error) {
      console.error('Error searching ICD:', error);
      setIcdSearchResults([]);
    }
  };

  // Function to open ICD search dialog
  const openIcdSearchDialog = () => {
    setIcdSearchQuery('');
    setIcdSearchResults([]);
    setShowIcdSearchDialog(true);
  };

  // Add ICD code to diagnoses field
  const addIcdToField = (icdCode, fieldName) => {
    setManualEvaluation(prev => {
      const currentValue = prev[fieldName];
      const newValue = currentValue ? `${currentValue}, ${icdCode}` : icdCode;
      return { ...prev, [fieldName]: newValue };
    });
    setShowIcdSearchDialog(false);
  };

  // Define a common style for all radio groups
  const radioGroupStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
    alignItems: 'center',
    height: '40px', // Force consistent height
    '& .MuiRadio-root': {
      padding: '0 8px' // Reduce padding to ensure alignment
    }
  };

  // Add this to the JSX near the diagnoses fields
  const renderIcdSearchButton = (fieldName) => (
    icdDatabaseAvailable && (
      <Button
        variant="outlined"
        color="neutral"
        size="sm"
        onClick={() => {
          setSelectedField(fieldName); // Reuse the selectedField state for both services and ICD
          openIcdSearchDialog();
        }}
        sx={{ ml: 1 }}
      >
        ICD suchen
      </Button>
    )
  );

  // Add the ICD search modal
  const renderIcdSearchModal = () => (
    <Modal open={showIcdSearchDialog} onClose={() => setShowIcdSearchDialog(false)}>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          p: 3,
          width: '80%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Typography level="h4" sx={{ mb: 2 }}>ICD-10 Code suchen</Typography>
        {/* Neu: Statusanzeige */}
        {(icdLoadingProgress.status === 'loading' || icdLoadingProgress.status === 'starting') && (
          <Typography level="body2" sx={{ mb: 1 }}>
            {icdLoadingProgress.message} {icdLoadingProgress.progress ? `(${Math.round(icdLoadingProgress.progress)}%)` : ''}
          </Typography>
        )}
        {icdLoadingProgress.status === 'error' && (
          <Typography level="body2" color="danger" sx={{ mb: 1 }}>
            {icdLoadingProgress.message}
          </Typography>
        )}
        {icdLoadingProgress.status === 'complete' && icdSearchResults.length === 0 && !icdSearchQuery && (
          <Typography level="body3" sx={{ mb: 1 }}>
            Datenbank geladen. Bitte Suchbegriff eingeben.
          </Typography>
        )}
        <Box sx={{ mb: 2 }}>
          <Input
            placeholder="ICD-10 Code oder Text eingeben"
            value={icdSearchQuery}
            onChange={(e) => {
              setIcdSearchQuery(e.target.value);
              handleIcdSearch(e.target.value);
            }}
            fullWidth
            disabled={!icdDatabaseAvailable || icdLoadingProgress.status === 'loading'}
          />
        </Box>
        
        <Sheet variant="outlined" sx={{ height: '400px', overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '100px' }}>Code</th>
                <th>Beschreibung</th>
                <th style={{ width: '80px' }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {icdSearchResults.map((result) => (
                <tr key={result.code}>
                  <td>{result.code}</td>
                  <td>{result.text}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() => addIcdToField(result.code, selectedField)}
                    >
                      Hinzufügen
                    </Button>
                  </td>
                </tr>
              ))}
              {icdSearchResults.length === 0 && icdSearchQuery && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center' }}>
                    Keine Ergebnisse gefunden
                  </td>
                </tr>
              )}
              {!icdSearchQuery && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center' }}>
                    Geben Sie einen Suchbegriff ein
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Sheet>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={() => setShowIcdSearchDialog(false)}>
            Schließen
          </Button>
        </Box>
        
        {/* Add the attribution notice */}
        <IcdAttribution />
      </Box>
    </Modal>
  );

  // Add state for the floating button
  const [showFloatingButton, setShowFloatingButton] = useState(true);

  // Add event listener for scroll events to hide the floating button when near the submit button
  useEffect(() => {
    const handleScroll = () => {
      const submitButton = document.getElementById('main-submit-button');
      if (submitButton) {
        const buttonRect = submitButton.getBoundingClientRect();
        // If the regular submit button is visible, hide the floating one
        setShowFloatingButton(
          buttonRect.top > window.innerHeight || buttonRect.bottom < 0
        );
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Run once to set initial state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Add state for batch evaluation features
  const [showBatchMode, setShowBatchMode] = useState(false);
  const [selectedEvaluations, setSelectedEvaluations] = useState([]);
 // eslint-disable-next-line
  const [batchResults, setBatchResults] = useState([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Toggle between single evaluation and batch mode
  const toggleBatchMode = () => {
    setShowBatchMode(prev => !prev);
    // Reset selected evaluations when switching to batch mode
    if (!showBatchMode) {
      setSelectedEvaluations([]);
    }
  };

  // Handle checkbox selection of evaluations for batch processing
  const handleEvaluationCheckboxChange = (evalName) => {
    setSelectedEvaluations(prev => {
      if (prev.includes(evalName)) {
        return prev.filter(name => name !== evalName);
      } else {
        return [...prev, evalName];
      }
    });
  };

  // Use the shared prepareEvaluationContent defined earlier (with normalization)

  // Execute all selected evaluations in sequence
  const handleBatchSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      alert('Bitte wählen Sie mindestens eine Datei aus.');
      return;
    }

    if (selectedEvaluations.length === 0) {
      alert('Bitte wählen Sie mindestens eine Auswertung aus.');
      return;
    }

    if (!TeaVM || !TeaVM.backend) {
      alert('WebAssembly runtime or module is not loaded yet.');
      return;
    }

    setIsBatchProcessing(true);
    setBatchProgress({ current: 0, total: selectedEvaluations.length });

    // Array to hold results for each evaluation
    const allBatchResults = [];

    try {
      // Get the authenticated BSNR
      const bsnrToUse = localStorage.getItem('authenticatedBsnr') || '';

      // Process each selected evaluation
      for (let i = 0; i < selectedEvaluations.length; i++) {
        const evaluationName = selectedEvaluations[i];
        setBatchProgress({ current: i + 1, total: selectedEvaluations.length });

        const evaluationContent = prepareEvaluationContent(evaluationName);
        if (!evaluationContent) {
          allBatchResults.push({
            evaluationName,
            error: 'Auswertungsinhalt konnte nicht geladen werden.',
            results: []
          });
          continue;
        }

        // Process all files for this evaluation
        const evaluationResults = [];
        for (const file of files) {
          // Read the file content using ISO-8859-15 encoding
          const fileContent = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const decoder = new TextDecoder('ISO-8859-15');
              resolve(decoder.decode(reader.result));
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
          });

          // Call WebAssembly backend
          const result = TeaVM.backend.instance.exports.process.value(fileContent, evaluationContent, bsnrToUse);

          // Parse the result
          const parsedResult = parseResultString(result);

          // Apply anonymization if demo mode is active
          if (demoMode && parsedResult.patients) {
            parsedResult.patients = anonymizePatientData(parsedResult.patients);
          }

          // Handle license errors
          if (parsedResult.error && parsedResult.error.includes('BSNR')) {
            alert(`Fehler bei der Datei "${file.name}": ${parsedResult.error}`);
            continue;
          }

          // Fix potential data issues
          if (parsedResult.totalPatients === undefined) {
            const directMatch = result.match(/TOTAL:(\d+)/);
            if (directMatch && directMatch.length > 1) {
              parsedResult.totalPatients = parseInt(directMatch[1], 10);
            } else if (parsedResult.matchingPatients > 0) {
              parsedResult.totalPatients = parsedResult.matchingPatients;
            }
          }

          if (parsedResult.totalPatients < parsedResult.matchingPatients) {
            parsedResult.totalPatients = parsedResult.matchingPatients;
          }

          // Store parsed result with filename
          evaluationResults.push({ 
            name: file.name, 
            ...parsedResult
          });
        }

        // Get the deserialized evaluation details
        let evaluationDetails = { evaluationName };

        try {
          evaluationDetails = deserializeEvaluation(evaluationContent) || { evaluationName };
        } catch (error) {
          console.error(`Failed to deserialize evaluation: ${evaluationName}`, error);
        }

        // Add to batch results
        allBatchResults.push({
          evaluationName,
          evaluationDetails,
          results: evaluationResults
        });
      }

      // Store all batch results
      // eslint-disable-next-line no-unused-vars
      setBatchResults(allBatchResults);

      // Format combined results for display, now including patient details
      const formattedResults = allBatchResults.map(evalResult => {
        const { evaluationName, results } = evalResult;
        let output = `Auswertung: ${evaluationName}\n`;
        output += `${'-'.repeat(40)}\n`;

        const totalMatchingPatients = results.reduce((sum, r) => sum + (r.matchingPatients || 0), 0);
        const totalPatients = results.reduce((sum, r) => sum + (r.totalPatients || 0), 0);

        output += `Gesamtergebnis: ${totalMatchingPatients} von ${totalPatients} Patienten gefunden\n\n`;

        for (const r of results) {
          if (r.error) {
            output += `Datei: ${r.name}\nFehler: ${r.error}\n\n`;
            continue;
          }

          output += `Datei: ${r.name}\n`;
          output += `Gesamtpatienten: ${r.totalPatients}\n`;
          output += `Passende Patienten: ${r.matchingPatients}\n`;
          
          // Add patient details for batch evaluation results
          if (r.patients && r.patients.length > 0) {
            output += '\nPatientenliste:\n';
            for (let idx = 0; idx < r.patients.length; idx++) {
              const patient = r.patients[idx];
              // Format birthdate if it's in YYYYMMDD format
              let birthdate = patient.birthdate || "";
              if (birthdate && birthdate.match(/^\d{8}$/)) {
                const year = birthdate.substring(0, 4);
                const month = birthdate.substring(4, 6);
                const day = birthdate.substring(6, 8);
                birthdate = `${day}.${month}.${year}`;
              }
              
              output += `${idx + 1}. ${patient.firstname} ${patient.lastname}, `;
              output += `Alter: ${patient.age} (geb. ${birthdate})\n`;
            }
          }
          
          output += '\n';
        }

        return output;
      }).join('\n' + '='.repeat(60) + '\n\n');

      setResult(formattedResults);

      // Generate PDF with combined results
      setTimeout(() => {
        console.log('Generating PDF for batch evaluation results');

        // First update the app name in the PDF generator
        setPdfAppName(appName);

        // Get the authenticated BSNR if available
        const bsnr = localStorage.getItem('authenticatedBsnr') || '';

        // Create timestamp format DD-MM-YYYY_HH-MM
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const timestamp = `${day}-${month}-${year}_${hours}-${minutes}`;

        const filename = `${appName}_Mehrfachauswertung${bsnr ? '_BSNR-' + bsnr : ''}_${timestamp}.pdf`;

        // Ensure logo is preloaded
        const fullLogoUrl = window.location.origin + logoUrl;

        preloadLogoImage(fullLogoUrl)
          .then(() => {
            // Generate batch results PDF with demo mode flag
            const pdf = generateBatchResultPdf(allBatchResults, appName, demoMode);

            // Set PDF document properties
            pdf.setProperties({
              title: `${appName} - Mehrfachauswertung - ${timestamp}`,
              creator: appName,
              author: appName
            });

            // Open in new tab with formatted filename
            openPdfInNewTab(pdf, filename);
          })
          .catch(error => {
            console.error('Error preloading logo for batch PDF:', error);
            const pdf = generateBatchResultPdf(allBatchResults, appName, demoMode);
            pdf.setProperties({
              title: `${appName} - Mehrfachauswertung - ${timestamp}`,
              creator: appName,
              author: appName
            });
            openPdfInNewTab(pdf, filename);
          });
      }, 500);

    } catch (error) {
      console.error('Error processing batch evaluations:', error);
      alert('Fehler bei der Verarbeitung der Auswertungen: ' + error.message);
    } finally {
      setIsBatchProcessing(false);
    }
  };

  // Function to select all evaluations
  const handleSelectAllEvaluations = () => {
    // Get all evaluation names from mergedEvaluations
    const allEvaluationNames = mergedEvaluations.map(item => item.name);
    setSelectedEvaluations(allEvaluationNames);
  };
  
  // Function to deselect all evaluations
  const handleDeselectAllEvaluations = () => {
    setSelectedEvaluations([]);
  };

  // Function to open the manual PDF
  const handleOpenManual = () => {
    // Open the manual PDF in a new tab
    window.open('/resources/QuEry-Manual.pdf', '_blank');
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* Remove LogoManager component - no longer needed */}
      
      <CssBaseline />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logoUrl} alt="Logo" style={{ height: 50, marginRight: 16 }} />
          <Typography level="h1">{appName}</Typography>
        </Box>
        
        {/* Add management buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Add a button to open the manual */}
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleOpenManual}
          >
            Anleitung
          </Button>

          {/* Add download button for query-find beta */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              component="a"
              href="/query-find-beta.zip"
              download
              variant="outlined"
              color="primary"
              sx={{
                backgroundColor: '#e3f2fd',
                borderColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#bbdefb',
                  borderColor: '#1976d2'
                }
              }}
            >
              QuEry-Find (Beta) herunterladen
            </Button>
            <IconButton
              variant="outlined"
              color="primary"
              size="sm"
              onClick={() => setShowPasswordInfo(true)}
              sx={{
                backgroundColor: '#e3f2fd',
                borderColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#bbdefb',
                  borderColor: '#1976d2'
                }
              }}
            >
              ℹ️
            </IconButton>
          </Box>

          {/* Add Demo Mode toggle */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            backgroundColor: demoMode ? '#e3f2fd' : 'transparent', 
            padding: '4px 8px',
            borderRadius: '4px',
            border: demoMode ? '1px solid #2196f3' : '1px solid transparent'
          }}>
            <input
              type="checkbox"
              id="demo-mode-toggle"
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <label htmlFor="demo-mode-toggle" style={{ 
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: demoMode ? '#1976d2' : 'inherit'
            }}>
              Demo-Modus {demoMode ? '(aktiv)' : ''}
            </label>
          </Box>
          
          <Button 
            component={Link} 
            to="/manage-evaluations"
            variant="outlined"
            color="neutral"
          >
            Auswertungen verwalten
          </Button>
          <Button
            variant="outlined"
            color="danger"
            onClick={onLogout}
          >
            Abmelden
          </Button>
        </Box>
      </Box>

      {/* Info box for QuEry-Find Beta */}
      {showBetaHint && (
        <Alert
          color="primary"
          variant="soft"
          sx={{
            maxWidth: 800,
            margin: '16px auto',
            backgroundColor: '#e3f2fd',
            borderColor: '#2196f3',
            position: 'relative'
          }}
          endDecorator={
            <IconButton
              variant="plain"
              size="sm"
              color="primary"
              onClick={() => {
                setShowBetaHint(false);
                localStorage.setItem('showBetaHint', 'false');
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8
              }}
            >
              ✕
            </IconButton>
          }
        >
          <Box>
            <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 'bold' }}>
              QuEry-Find (Beta) - Suchprogramm für .con-Dateien
            </Typography>
            <Typography level="body-sm">
              Mit dem obigen Button können Sie die Beta-Version von QuEry-Find herunterladen.
              Dies ist eine eigenständige Windows-Anwendung (.exe), die das Suchen und Finden von
              .con-Dateien auf Ihrem Computer erleichtert. Die Software befindet sich noch in der
              Beta-Phase und wird kontinuierlich weiterentwickelt.
            </Typography>
            <Typography level="body-sm" sx={{ mt: 1, fontWeight: 'bold', color: '#1976d2' }}>
              Das ZIP-Archiv ist passwortgeschützt. Passwort: QuE
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Password Info Modal */}
      <Modal
        open={showPasswordInfo}
        onClose={() => setShowPasswordInfo(false)}
      >
        <Sheet
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 400,
            maxWidth: 500,
            borderRadius: 'md',
            p: 3,
            boxShadow: 'lg',
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>
            QuEry-Find (Beta) - Download-Information
          </Typography>
          <Typography level="body-md" sx={{ mb: 2 }}>
            Die heruntergeladene ZIP-Datei enthält das QuEry-Find Suchprogramm für .con-Dateien.
          </Typography>
          <Box sx={{
            backgroundColor: '#e3f2fd',
            padding: 2,
            borderRadius: 'md',
            border: '1px solid #2196f3',
            mb: 2
          }}>
            <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Passwort zum Entpacken:
            </Typography>
            <Typography
              level="h3"
              sx={{
                color: '#1976d2',
                fontFamily: 'monospace',
                userSelect: 'all'
              }}
            >
              QuE
            </Typography>
          </Box>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            Tipp: Sie können das Passwort mit der Maus markieren und kopieren.
          </Typography>
          <Button
            onClick={() => setShowPasswordInfo(false)}
            fullWidth
            color="primary"
          >
            Schließen
          </Button>
        </Sheet>
      </Modal>

      <Box
        component="form"
        onSubmit={vorhaltepauschaleOnlyMode ? handleVorhaltepauschaleOnlySubmit : (showBatchMode ? handleBatchSubmit : handleSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: 600,
          margin: '0 auto',
          padding: 2,
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#fff',
        }}
      >
        {/* Add a toggle switch for batch mode and Vorhaltepauschale-only mode */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2">Auswertungen</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={!showBatchMode && !vorhaltepauschaleOnlyMode ? "solid" : "outlined"}
              color={!showBatchMode && !vorhaltepauschaleOnlyMode ? "primary" : "neutral"}
              onClick={() => {
                setShowBatchMode(false);
                setVorhaltepauschaleOnlyMode(false);
              }}
              size="sm"
            >
              Einzelauswertung
            </Button>
            <Button
              variant={showBatchMode ? "solid" : "outlined"}
              color={showBatchMode ? "primary" : "neutral"}
              onClick={() => {
                setShowBatchMode(true);
                setVorhaltepauschaleOnlyMode(false);
              }}
              size="sm"
            >
              Mehrfachauswertung
            </Button>
            <Button
              variant={vorhaltepauschaleOnlyMode ? "solid" : "outlined"}
              color={vorhaltepauschaleOnlyMode ? "success" : "neutral"}
              onClick={() => {
                setShowBatchMode(false);
                setVorhaltepauschaleOnlyMode(true);
              }}
              size="sm"
            >
              Nur Vorhaltepauschale
            </Button>
          </Box>
        </Box>
        
        {/* Show different UI based on mode */}
        {vorhaltepauschaleOnlyMode ? (
          // Vorhaltepauschale-only mode - no evaluation selection needed
          <Box sx={{ padding: 2, border: '2px solid #4CAF50', borderRadius: '4px', backgroundColor: '#f1f8f4' }}>
            <Typography level="h3" sx={{ color: '#4CAF50', mb: 1 }}>
              Nur Vorhaltepauschale-Modus
            </Typography>
            <Typography level="body2">
              In diesem Modus werden nur Vorhaltepauschale-Statistiken berechnet, ohne Patienten-Filterung oder -Anzeige.
            </Typography>
            <Typography level="body2" sx={{ mt: 1 }}>
              <strong>Hinweis:</strong> Bitte wählen Sie eine oder mehrere Dateien aus und geben Sie das Quartal für jede Datei an.
            </Typography>
            {vorhaltepauschaleKriterien.length > 0 && (
              <Typography level="body-xs" sx={{ mt: 1, color: '#666' }}>
                ({vorhaltepauschaleKriterien.filter(k => k.active === 1).length} aktive Kriterien)
              </Typography>
            )}

            {/* Info about criteria 9 and 10 */}
            <Box sx={{
              p: 2,
              mt: 2,
              backgroundColor: '#e3f2fd',
              border: '1px solid #1976d2',
              borderRadius: '8px'
            }}>
              <Typography
                level="body-sm"
                sx={{
                  color: '#1565c0'
                }}
              >
                Hinweis: Die Kriterien 9 (Zusammenarbeit) und 10 (Sprechstunden/Öffnungszeiten) können nicht automatisch ausgewertet werden.
              </Typography>
            </Box>
          </Box>
        ) : !showBatchMode ? (
          // Single evaluation mode - existing code
          <>
            <Select
              value={isCustomEvaluation ? "custom" : evaluation}
              onChange={(e, newValue) => handleEvaluationSelect(newValue)}
              defaultValue="custom"
              placeholder="Bitte auswählen"
            >
              <Option value="custom">Benutzerdefinierte Auswertung</Option>
              {mergedEvaluations.map((evaluationItem) => (
                <Option key={evaluationItem.name} value={evaluationItem.name}>
                  {evaluationItem.isCustom ? `${evaluationItem.name} (Benutzerdefiniert)` : evaluationItem.name}
                </Option>
              ))}
            </Select>

            {isCustomEvaluation && (
              <Box>
                <Typography>Name der benutzerdefinierten Auswertung:</Typography>
                <Input
                  name="evaluationName"
                  placeholder="Name der Auswertung"
                  value={manualEvaluation.evaluationName}
                  onChange={handleManualInputChange}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSaveCustomEvaluation}
                  sx={{ mt: 1 }}
                >
                  Auswertung speichern
                </Button>
                {manualEvaluation.id && (
                  <Button
                    variant="outlined"
                    color="danger"
                    onClick={() => handleDeleteCustomEvaluation(manualEvaluation.id)}
                    sx={{ mt: 1 }}
                  >
                    Auswertung löschen
                  </Button>
                )}
              </Box>
            )}
          </>
        ) : (
          // Batch mode - multiple selection with checkboxes
          <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography>Wählen Sie mehrere Auswertungen aus:</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="sm"
                  variant="outlined"
                  color="primary"
                  onClick={handleSelectAllEvaluations}
                >
                  Alle auswählen
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  onClick={handleDeselectAllEvaluations}
                >
                  Alle entfernen
                </Button>
              </Box>
            </Box>
            <List>
              {/* Only show standard and custom evaluations, not the custom option */}
              {mergedEvaluations.map((evalItem) => (
                <ListItem key={evalItem.name}>
                  <input
                    type="checkbox"
                    id={`eval-${evalItem.name}`}
                    checked={selectedEvaluations.includes(evalItem.name)}
                    onChange={() => handleEvaluationCheckboxChange(evalItem.name)}
                    style={{ marginRight: '10px' }}
                  />
                  <label htmlFor={`eval-${evalItem.name}`}>
                    {evalItem.isCustom ? `${evalItem.name} (Benutzerdefiniert)` : evalItem.name}
                  </label>
                </ListItem>
              ))}
            </List>
            {selectedEvaluations.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography>
                  {selectedEvaluations.length} Auswertung{selectedEvaluations.length !== 1 ? 'en' : ''} ausgewählt
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box>
          <Typography level="h2">Abrechnungsdatei(en)</Typography>
          <Box 
            sx={{ 
              border: '2px dashed #ccc', 
              borderRadius: '4px', 
              padding: 2, 
              backgroundColor: '#f8f8f8',
              textAlign: 'center',
              mb: 2,
              cursor: 'pointer'
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <Typography>
              Dateien hier ablegen (Drag & Drop) oder klicken
              <br />
              <span style={{ fontSize: '0.8em', color: '#666' }}>
                (Maximal 4 Dateien)
              </span>
            </Typography>
            <Input 
              id="file-upload"
              type="file" 
              onChange={handleFileSelect} 
              multiple
              sx={{ display: 'none' }}
            />
          </Box>
          
          {/* Display selected files with remove option */}
          {files.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography level="body2" fontWeight="bold">Ausgewählte Dateien:</Typography>
              {files.map((file, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
                  <Typography level="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                  {vorhaltepauschaleOnlyMode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography level="body-xs" sx={{ minWidth: '40px' }}>Quartal:</Typography>
                      <Select
                        value={(fileQuarters[index] || 1).toString()}
                        onChange={(e, newValue) => updateFileQuarter(index, parseInt(newValue))}
                        size="sm"
                        sx={{ width: '80px' }}
                      >
                        <Option value="1">Q1</Option>
                        <Option value="2">Q2</Option>
                        <Option value="3">Q3</Option>
                        <Option value="4">Q4</Option>
                      </Select>
                    </Box>
                  )}
                  <Button
                    size="sm"
                    color="danger"
                    variant="soft"
                    onClick={() => removeFile(index)}
                  >
                    Entfernen
                  </Button>
                </Box>
              ))}
            </Box>
          )}

          {/* Important notices for Vorhaltepauschale mode */}
          {vorhaltepauschaleOnlyMode && files.length > 0 && (
            <Box sx={{ mb: 2, mt: 2 }}>
              {/* Red warning about Q4 quarter selection */}
              <Box sx={{
                p: 2,
                backgroundColor: '#ffebee',
                border: '2px solid #d32f2f',
                borderRadius: '8px'
              }}>
                <Typography
                  level="h3"
                  sx={{
                    color: '#d32f2f',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    mb: 1
                  }}
                >
                  WICHTIG: Quartalswahl bei 4. Quartal beachten!
                </Typography>
                <Typography
                  level="body-sm"
                  sx={{
                    color: '#c62828',
                    textAlign: 'center'
                  }}
                >
                  Bei der Auswertung des 4. Quartals ist die korrekte Quartalswahl entscheidend für die Berechnung.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Show advanced options only for single evaluation mode, not in Vorhaltepauschale-only mode */}
        {!showBatchMode && !vorhaltepauschaleOnlyMode && (
          <>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="solid" color="primary" onClick={toggleAdvancedView}>
                {showAdvancedView ? "Erweiterte Ansicht ausblenden" : "Erweiterte Ansicht"}
              </Button>
            </Box>

            {showAdvancedView && (
              <>
                <Typography level="h3">Konsultationszeitraum:</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography>Vom</Typography>
                  <Input
                    type="date"
                    name="dateSpanStart"
                    value={manualEvaluation.dateSpanStart}
                    onChange={handleManualInputChange}
                    disabled={!isCustomEvaluation && !!evaluation}
                  />
                  <Typography>Bis</Typography>
                  <Input
                    type="date"
                    name="dateSpanEnd"
                    value={manualEvaluation.dateSpanEnd}
                    onChange={handleManualInputChange}
                    disabled={!isCustomEvaluation && !!evaluation}
                  />
                </Box>

                <Typography level="h3">Alter:</Typography>
                <Select
                  name="ageOperator"
                  value={manualEvaluation.ageOperator.toString()}
                  onChange={handleAgeOperatorChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                  sx={{ mb: 1 }}
                >
                  <Option value="0">Jünger als</Option>
                  <Option value="1">Genau</Option>
                  <Option value="2">Älter als</Option>
                  <Option value="3">Zwischen</Option>
                </Select>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography>{manualEvaluation.ageOperator === 3 ? "Von" : "Alter"}</Typography>
                  <Input
                    type="number"
                    name="ageFrom"
                    value={manualEvaluation.ageFrom}
                    onChange={handleManualInputChange}
                    disabled={!isCustomEvaluation && !!evaluation}
                    sx={{ width: '80px' }}
                  />
                  {manualEvaluation.ageOperator === 3 && (
                    <>
                      <Typography>Bis</Typography>
                      <Input
                        type="number"
                        name="ageTo"
                        value={manualEvaluation.ageTo}
                        onChange={handleManualInputChange}
                        disabled={!isCustomEvaluation && !!evaluation}
                        sx={{ width: '80px' }}
                      />
                    </>
                  )}
                  <Typography>Jahre</Typography>
                </Box>

                <Typography>Mit Basis-Gebührenordnungsposition(en) im Zeitraum</Typography>
                <Input
                  type="checkbox"
                  name="baseServicesWithinDateSpan"
                  checked={manualEvaluation.baseServicesWithinDateSpan}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />

                <Typography>Mit Gebührenordnungsposition(en) im Zeitraum</Typography>
                <Input
                  type="checkbox"
                  name="servicesWithinDateSpan"
                  checked={manualEvaluation.servicesWithinDateSpan}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />



                <Typography>Ohne Gebührenordnungsposition(en) im Zeitraum</Typography>
                <Input
                  type="checkbox"
                  name="notServicesWithinDateSpan"
                  checked={manualEvaluation.notServicesWithinDateSpan}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />

                <Typography>Mit Diagnose(n) im Zeitraum</Typography>
                <Input
                  type="checkbox"
                  name="diagnosesWithinDateSpan"
                  checked={manualEvaluation.diagnosesWithinDateSpan}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />

                <Typography>Ohne Diagnose(n) im Zeitraum</Typography>
                <Input
                  type="checkbox"
                  name="notDiagnosesWithinDateSpan"
                  checked={manualEvaluation.notDiagnosesWithinDateSpan}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />
                
                {/* Eingebaut: Institutionskennzeichen-Filter  */}
                <Typography level="h3">Mit Institutionskennzeichen:</Typography>
                <RadioGroup
                  name="institutionIdsOperator"
                  value={manualEvaluation.institutionIdsOperator.toString()}
                  onChange={(e) => handleOperatorChange(e, e.target.value)}
                  disabled={!isCustomEvaluation && !!evaluation}
                  orientation="horizontal"
                  sx={radioGroupStyle}
                >
                  <Radio value="0" label="UND" />
                  <Radio value="1" label="ODER" />
                </RadioGroup>
                <Input
                  name="institutionIds"
                  placeholder="Institutionskennzeichen"
                  value={manualEvaluation.institutionIds}
                  onChange={handleManualInputChange}
                  disabled={!isCustomEvaluation && !!evaluation}
                />

                <Typography level="h3">Quartals-übergreifende Auswertung:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                   
                    name="excludeIfFoundAcrossQuarters"
                    checked={Boolean(manualEvaluation.excludeIfFoundAcrossQuarters)}
                    onChange={(e) => {
                      setManualEvaluation(prev => ({
                        ...prev,
                        excludeIfFoundAcrossQuarters: e.target.checked
                      }));
                    }}
                    disabled={!isCustomEvaluation && !!evaluation}
                  />
                  <Typography level="body2">
                    Patienten ausschließen, die in anderen Quartalen gefunden wurden (für Impfstatus-Abfragen)
                  </Typography>
                </Box>
              </>
            )}
            {/* Entfernt: doppelte Quartals-übergreifende Auswertung außerhalb der erweiterten Ansicht */}
            <Typography level="h3">Mit Basis-Gebührenordnungsposition(en):</Typography>
            <RadioGroup

              name="baseServicesOperator"
              value={manualEvaluation.baseServicesOperator.toString()}

              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={!isCustomEvaluation && !!evaluation}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="0" label="UND" />
              <Radio value="1" label="ODER" />
            </RadioGroup>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Input
                name="baseServices"
                placeholder="Basis-Gebührenordnungsposition(en)"
                value={manualEvaluation.baseServices}
                onChange={handleManualInputChange}
                disabled={!isCustomEvaluation && !!evaluation}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => openServicesDialog('baseServices')}
                disabled={!isCustomEvaluation && !!evaluation}
                sx={{ minWidth: 'auto' }}
              >
                Auswählen
              </Button>
            </Box>

            <Typography level="h3">Mit Gebührenordnungsposition(en):</Typography>
            <RadioGroup
              name="servicesOperator"
              value={manualEvaluation.servicesOperator.toString()}
              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={!isCustomEvaluation && !!evaluation}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="0" label="UND" />
              <Radio value="1" label="ODER" />
            </RadioGroup>
            <Input
              name="services"
              placeholder="Gebührenordnungsposition(en)"
              value={manualEvaluation.services}
              onChange={handleManualInputChange}
              disabled={!isCustomEvaluation && !!evaluation}
            />

            <Typography level="h3">Ohne Gebührenordnungsposition(en):</Typography>
            <RadioGroup
              name="notServicesOperator"
              value={manualEvaluation.notServicesOperator.toString()}
              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={!isCustomEvaluation && !!evaluation}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="2" label="Nicht-UND" />
              <Radio value="3" label="Nicht-ODER" />
            </RadioGroup>
            <Input
              name="notServices"
              placeholder="Ohne Gebührenordnungsposition(en)"
              value={manualEvaluation.notServices}
              onChange={handleManualInputChange}
              disabled={!isCustomEvaluation && !!evaluation}
            />

            <Typography level="h3">Mit Diagnose(n):</Typography>
            <RadioGroup
              name="diagnosesOperator"
              value={manualEvaluation.diagnosesOperator.toString()}
              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={!isCustomEvaluation && !!evaluation}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="0" label="UND" />
              <Radio value="1" label="ODER" />
            </RadioGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Input
                name="diagnoses"
                placeholder="Diagnosen"
                value={manualEvaluation.diagnoses}
                onChange={handleManualInputChange}
                disabled={!isCustomEvaluation && !!evaluation}
                sx={{ flexGrow: 1 }}
              />
              {renderIcdSearchButton('diagnoses')}
            </Box>

            <Typography level="h3">Ohne Diagnose(n):</Typography>
            <RadioGroup
              name="notDiagnosesOperator"
              value={manualEvaluation.notDiagnosesOperator.toString()}
              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={!isCustomEvaluation && !!evaluation}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="2" label="Nicht-UND" />
              <Radio value="3" label="Nicht-ODER" />
            </RadioGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Input
                name="notDiagnoses"
                placeholder="Ohne Diagnosen"
                value={manualEvaluation.notDiagnoses}
                onChange={handleManualInputChange}
                disabled={!isCustomEvaluation && !!evaluation}
                sx={{ flexGrow: 1 }}
              />
              {renderIcdSearchButton('notDiagnoses')}
            </Box>
            {/* Neu: Hinweis zur ICD-Suche */}
            {!icdDatabaseAvailable && icdLoadingProgress.status !== 'error' && (
              <Typography level="body3" sx={{ mt: 1 }}>
                ICD-Suche wird initialisiert... {icdLoadingProgress.progress ? `${Math.round(icdLoadingProgress.progress)}%` : ''}
              </Typography>
            )}
            {icdLoadingProgress.status === 'error' && (
              <Typography level="body3" color="danger" sx={{ mt: 1 }}>
                {icdLoadingProgress.message}
              </Typography>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            id="main-submit-button"
            type="submit"
            variant="solid"
            color={vorhaltepauschaleOnlyMode ? "success" : "primary"}
            disabled={isBatchProcessing}
          >
            {vorhaltepauschaleOnlyMode
              ? "Vorhaltepauschale berechnen"
              : (showBatchMode
                ? (isBatchProcessing ? `Auswertung ${batchProgress.current}/${batchProgress.total}...` : "Ausgewählte Auswertungen starten")
                : "Auswerten")
            }
          </Button>
        </Box>
        <Box>
          <Typography level="h2">Ergebnis</Typography>
          <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
            {result}
          </pre>
           
          {result && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button  
                variant="outlined" 
                color="primary"
                onClick={handleGeneratePdf}
                startDecorator={<span role="img" aria-label="pdf">📄</span>}
              >
                PDF anzeigen
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleDownloadPdf}
                startDecorator={<span role="img" aria-label="download">💾</span>}
              >
                PDF herunterladen
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Services selection modal */}
      <Modal
        open={isServicesDialogOpen}
        onClose={() => setIsServicesDialogOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '8px',
            p: 3,
            width: '80%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>Gebührenordnungspositionen auswählen</Typography>
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {services.map((service, index) => (
              <ListItem key={index}>
                <ListItemButton 
                  onClick={() => handleServiceSelect(service)}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography fontWeight="bold">{service.code}</Typography>
                  <Typography>{service.description}</Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Button 
            onClick={() => setIsServicesDialogOpen(false)}
            sx={{ mt: 2 }}
            variant="outlined"
            color="neutral"
          >
            Schließen
          </Button>
        </Box>
      </Modal>

      {/* ICD search modal */}
      {renderIcdSearchModal()}

      {/* Add the floating submit button - adapt for batch mode and Vorhaltepauschale mode */}
      {showFloatingButton && (
        <Box
          sx={{
            position: 'fixed',
            right: '20px',
            bottom: '20px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Button
            variant="solid"
            color={vorhaltepauschaleOnlyMode ? "success" : "primary"}
            onClick={vorhaltepauschaleOnlyMode ? handleVorhaltepauschaleOnlySubmit : (showBatchMode ? handleBatchSubmit : handleSubmit)}
            disabled={isBatchProcessing}
            sx={{
              borderRadius: '24px',
              minWidth: '120px',
              height: '48px',
              padding: '0 16px',
              boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
              '&:hover': {
                boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
              },
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={vorhaltepauschaleOnlyMode ? "Vorhaltepauschale berechnen" : (showBatchMode ? "Ausgewählte Auswertungen starten" : "Daten auswerten")}
          >
            <span role="img" aria-label="auswerten" style={{ fontSize: '20px' }}>
              {vorhaltepauschaleOnlyMode ? '💚' : '📊'}
            </span>
            <span>{vorhaltepauschaleOnlyMode ? "Vorhaltepauschale" : "Auswerten"}</span>
          </Button>
        </Box>
      )}
    </Box>
  );
}

// Main App component with routing
function App() {
  const [showVerification, setShowVerification] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Get app name from preloaded settings
  const [appName, setAppName] = useState(window.appSettings?.appName || 'QuEry-Tool');
  // Define logoUrl directly instead of using state since we don't need to update it
  const logoUrl = `/logo.svg?t=${Date.now()}`; // Add cache busting

  // Set document title to app name
  useEffect(() => {
    document.title = isAdminRoute ? `${appName} Admin` : appName;
    
    // Only fetch if settings weren't preloaded
    if (!window.appSettings) {
      fetch('/api/appearance/settings')
        .then(response => response.json())
        .then(data => {
          if (data.appName) {
            setAppName(data.appName);
            document.title = isAdminRoute ? `${data.appName} Admin` : data.appName;
            // Store for later use
            window.appSettings = data;
          }
        })
        .catch(error => {
          console.error('Error loading app settings:', error);
        });
    }
    
    // Update favicon with cache busting
    const linkTag = document.querySelector('link[rel="icon"]') || document.createElement('link');
    linkTag.type = 'image/x-icon';
    linkTag.rel = 'icon';
    linkTag.href = `/favicon.ico?t=${Date.now()}`;
    if (!document.querySelector('link[rel="icon"]')) {
      document.head.appendChild(linkTag);
    }
  }, [isAdminRoute, appName]);

  // Only check BSNR authentication for non-admin routes
  useEffect(() => {
    async function checkAuth() {
      // Skip BSNR verification if we're on an admin route
      if (isAdminRoute) {
        setIsLoading(false);
        setShowVerification(false);
        return;
      }

      setIsLoading(true);
      
      try {
        const isAuthenticated = await checkAuthentication();
        
        if (isAuthenticated) {
          console.log("User is already authenticated via session");
          setShowVerification(false);
        } else {
          console.log("No valid session found, showing verification screen");
          setShowVerification(true);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        // If there's an error, show the verification screen
        setShowVerification(true);
      } finally {
        setIsLoading(false);
      }
    }
    
    checkAuth();
  }, [isAdminRoute, location.pathname]);

  const handleVerificationSuccess = () => {
    console.log("Verification successful, proceeding to main application");
    setShowVerification(false);
  };
  
  const handleLogout = async () => {
    await logout();
    setShowVerification(true);
  };

  // Show loading screen while checking authentication status
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <img src={logoUrl} alt="Logo" style={{ height: 80 }} />
        <Typography level="h3" className="app-name-placeholder">{appName} wird geladen...</Typography>
      </Box>
    );
  }

  // For admin routes, don't show BSNR verification
  if (isAdminRoute) {
    return (
      <Routes>
        <Route 
          path="/admin" 
          element={
            <ProtectedAdminRoute>
              <Admin />
            </ProtectedAdminRoute>
          } 
        />
        <Route path="/admin/login" element={<AdminLogin />} />
      </Routes>
    );
  }

  // For non-admin routes, apply BSNR verification as usual
  return (
    showVerification ? (
      <BsnrVerification onVerificationSuccess={handleVerificationSuccess} />
    ) : (
      <Routes>
        <Route path="/" element={<MainApp onLogout={handleLogout} />} />
        <Route path="/manage-evaluations" element={<ManageEvaluations />} />
      </Routes>
    )
  );
}

// Wrapper component to make useLocation work
function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;